import { useRef, useCallback, useState, useEffect, lazy, Suspense } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import { useGameState } from '@/hooks/useGameState';
import { useIsMobile } from '@/hooks/use-mobile';
import { KAIJU } from '@/lib/gameData';
import { getOutroImages } from '@/lib/gameDataOverrides';
import SetupScreen from '@/components/SetupScreen';
import LoadingBar from '@/components/LoadingBar';
import cityBg from '@/assets/city-destruction-bg.jpg';

import IntroSequence from '@/components/IntroSequence';
const TutorialOverlay = lazy(() => import('@/components/TutorialOverlay'));

const GameDashboard = lazy(() => import('@/components/GameDashboard'));
import PatientTriageFlow from '@/components/PatientTriageFlow';
const WaveTransition = lazy(() => import('@/components/WaveTransition'));
const WaveReview = lazy(() => import('@/components/WaveReview'));
const OutroSequence = lazy(() => import('@/components/OutroSequence'));
const EndScreen = lazy(() => import('@/components/EndScreen'));
const HighScoresScreen = lazy(() => import('@/components/HighScoresScreen'));

const Index = () => {
  const PhaseFallback = () => (
    <div className="min-h-screen bg-black" />
  );

  const game = useGameState();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isMobile = useIsMobile();
  const [muted, setMuted] = useState(true); // default true, updated once mobile detection resolves

  // Set initial mute based on device type (once hook resolves)
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && isMobile !== undefined) {
      initializedRef.current = true;
      setMuted(isMobile);
    }
  }, [isMobile]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = muted;
    }
  }, [muted]);

  const toggleMute = useCallback(() => setMuted(m => !m), []);

  // Prefetch outro images during gameplay so they're cached before the outro starts
  // Prefetch high scores connection when on setup screen
  useEffect(() => {
    if (game.phase === 'setup') {
      import('@/lib/highScoresCache').then(({ prefetchHighScores }) => {
        prefetchHighScores();
      });
    }
  }, [game.phase]);

  // Prefetch outro images during gameplay
  useEffect(() => {
    if (game.phase === 'playing') {
      const images = getOutroImages().filter(Boolean) as string[];
      images.forEach(src => {
        const img = new Image();
        img.src = src;
      });
    }
  }, [game.phase]);

  const handleMainMenu = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    game.resetGame();
  }, [game.resetGame]);

  return (
    <div
      className="h-dvh overflow-hidden bg-destruction relative"
      style={{ backgroundImage: `url(${cityBg})` }}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-background/90" />

      <div className="relative z-10 h-full overflow-hidden">
        {game.phase === 'setup' && (
          <SetupScreen onStart={game.startGame} onViewHighScores={() => game.showHighScores('view')} />
        )}

        {game.phase === 'loading' && (
          <LoadingBar onComplete={game.completeLoading} />
        )}

        {game.phase === 'intro' && game.kaijuId && (
          <IntroSequence
            kaijuId={game.kaijuId}
            onSkip={game.completeIntro}
            onComplete={game.completeIntro}
            muted={muted}
            onToggleMute={toggleMute}
          />
        )}

        <Suspense fallback={<PhaseFallback />}>

          {game.phase === 'tutorial' && game.kaijuId && (
            <TutorialOverlay
              onStart={game.startPlaying}
              kaijuName={KAIJU[game.kaijuId].name}
              algorithm={game.algorithm}
              cityName={game.cityName}
            />
          )}

          {game.phase === 'playing' && game.kaijuId && (
            <>
              <GameDashboard
                wave={game.wave}
                patients={game.visiblePatients}
                stats={game.stats}
                accuracy={game.accuracy}
                cityName={game.cityName}
                kaijuName={KAIJU[game.kaijuId].name}
                onSelectPatient={game.selectPatient}
                onPause={game.pauseGame}
                onResume={game.resumeGame}
                onReset={game.resetBattle}
                onMainMenu={handleMainMenu}
              />
              {game.selectedPatient && (
                <PatientTriageFlow
                  patient={game.selectedPatient}
                  onAssignTag={(tag, penalty, penaltyMessages) => game.assignTag(game.selectedPatient!.id, tag, penalty, penaltyMessages)}
                  onClose={() => game.selectPatient(null)}
                />
              )}
            </>
          )}

          {game.phase === 'wave-review' && game.kaijuId && (
            <WaveReview
              wave={game.wave}
              patients={game.patients}
              onContinue={game.completeWaveReview}
            />
          )}

          {game.phase === 'wave-transition' && game.kaijuId && (
            <WaveTransition
              wave={game.wave}
              kaijuId={game.kaijuId}
              onContinue={game.nextWave}
            />
          )}
          {game.phase === 'outro' && (
            <OutroSequence
              onSkip={game.completeOutro}
              onComplete={game.completeOutro}
              muted={muted}
              onToggleMute={toggleMute}
            />
          )}

          {game.phase === 'ended' && game.kaijuId && (
            <EndScreen
              stats={game.stats}
              accuracy={game.accuracy}
              kaijuId={game.kaijuId}
              playerName={game.playerName}
              onHighScores={game.showHighScores}
              audioRef={audioRef}
              muted={muted}
              onToggleMute={toggleMute}
            />
          )}

          {game.phase === 'highscores' && game.kaijuId && (
            <>
              <button
                onClick={toggleMute}
                className="absolute top-4 right-4 z-20 p-2 rounded-lg border border-border bg-background/80 backdrop-blur-sm hover:bg-muted/50 transition-colors"
                aria-label={muted ? 'Unmute' : 'Mute'}
              >
                {muted ? <VolumeX className="h-5 w-5 text-muted-foreground" /> : <Volume2 className="h-5 w-5 text-foreground" />}
              </button>
              <HighScoresScreen
                stats={game.stats}
                accuracy={game.accuracy}
                kaijuId={game.kaijuId}
                onMainMenu={handleMainMenu}
              />
            </>
          )}

          {game.phase === 'view-highscores' && (
            <HighScoresScreen
              stats={{ score: 0, correct: 0, wrong: 0, expired: 0, total: 0, livesSaved: 0, preventableDeteriorations: 0, preventableDeaths: 0, unnecessarySteps: 0 }}
              accuracy={0}
              kaijuId="rodan"
              onMainMenu={handleMainMenu}
              viewOnly
            />
          )}
        </Suspense>
      </div>
    </div>
  );
};

export default Index;
